import React from "react";
import { useDropzone } from "react-dropzone";
import { useField, useFormikContext } from "formik";
import { toast } from "react-toastify";
import { acceptedFileTypesArray } from "../../../components/forms/FileField";

export default function UploadFileField(props) {
  const {
    name,
    multiple = false,
    disabled = false,
    droppable = true,
    fileLimit = 10,
    acceptedTypes = acceptedFileTypesArray,
  } = props;
  const [meta] = useField(props);
  const { values, setFieldValue } = useFormikContext();

  const onDrop = (acceptedFiles) => {
    let fileList = [...(values[name] || [])];
  
    let totalSize = fileList.reduce((acc, file) => acc + file.size, 0);
  
    for (const file of acceptedFiles) {
      if (!acceptedTypes.includes(file.type)) {
        toast.error(
          <>
            <b>{file.type}</b> file type is not allowed
          </>
        );
        continue;
      }
  
      if (totalSize + file.size > 10 * 1024 * 1024) {
        toast.error("Total file size should not exceed 10MB.");
        return;
      }
  
      fileList.push(file);
      totalSize += file.size;
  
      if (props.onFileSelect) {
        props.onFileSelect(file);
      }
    }
  
    if (fileList.length > fileLimit) {
      toast.error(
        `You can only upload ${fileLimit} file${fileLimit > 1 ? "s" : ""}.`
      );
      return;
    }
  
    setFieldValue(name, fileList);
  };
  
  const removeFile = (event, index) => {
    event.stopPropagation();
    const updatedFileList = values[name]?.filter((_, idx) => idx !== index);
    setFieldValue(name, updatedFileList);
  };

  const { getRootProps, getInputProps } = useDropzone({
    multiple,
    disabled,
    onDropAccepted: onDrop,
  });

  return (
    <div>
      {droppable && (
        <div
          {...getRootProps({
            className: "file-field py-5",
          })}
        >
          <input name={name} {...getInputProps()} />
          <div className="mb-0 d-flex flex-column justify-content-center align-items-center">
            {values[name]?.length > 0 ? (
              <div className="d-flex flex-wrap justify-content-center">
                {values[name].map((file, idx) => (
                  <div
                    key={idx}
                    className="file-preview position-relative d-flex flex-column align-items-center m-2"
                    style={{ width: "100px", height: "100px" }}
                  >
                    <img
                      src="/images/vectors/close-grey.svg"
                      className="close-img position-absolute top-0 end-0 bg-white text-gray-1 border border-gray-1 rounded-4 mt-1 me-1 p-1"
                      aria-label="Remove"
                      onClick={(event) => removeFile(event, idx)}
                    />

                    {file.type.startsWith("image/") ? (
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="file-image mb-2"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "5px",
                        }}
                      />
                    ) : (
                      <span className="text-primary-600 mb-3">
                        <img
                          src="/images/eligibility/document-inactive.svg"
                          className="document-img"
                        />
                      </span>
                    )}
                    {!file.type.startsWith("image/") && (
                      <span className="file-name text-center">
                        {file.name.length > 7
                          ? `${file.name.slice(0, 7)}...`
                          : file.name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <img
                  src="/images/vectors/upload.svg"
                  alt=""
                  className="pathway-icon"
                />
                <p className="fs-6 fw-semibold mb-1 mt-2">
                  Drop file here or click to upload
                </p>
                <p className="text-gray-1 text-center mb-0">
                  Supported file formats: jpg, png, and pdf up to 10MB
                </p>
              </>
            )}
          </div>
        </div>
      )}
      {meta.touched && meta.error ? (
        <div className="invalid-feedback mb-0" style={{ display: "block" }}>
          {meta.error}
        </div>
      ) : null}
    </div>
  );
}
